import {
  FeeEstimationFailedError,
  InvalidParamError,
  SimulationFailedError,
  getActiveNetworkData,
  getDefaultClient,
} from '@dynamic-labs-sdk/client';
import { createApiClient, getCore } from '@dynamic-labs-sdk/client/core';
import {
  PublicKey,
  type Transaction,
  type VersionedTransaction,
} from '@solana/web3.js';
import bs58 from 'bs58';

import type { SolanaWalletAccount } from '../SolanaWalletAccount.types';
import { isSolanaWalletAccount } from '../isSolanaWalletAccount';
import type { SolanaSimulationResult } from '../transactionSimulation.types';
import { calculateSolanaTransactionFee } from '../utils/calculateSolanaTransactionFee';
import { isVersionedTransaction } from '../utils/isVersionedTransaction';

type SimulateSolanaTransactionParams = {
  includeFees?: boolean;
  transaction: Transaction | VersionedTransaction;
  walletAccount: SolanaWalletAccount;
};

/**
 * Simulates a Solana transaction to preview its effects before execution.
 *
 * This function uses the Blockaid API to analyze the transaction and returns
 * information about asset transfers, security validation, and optionally fee estimates.
 * The simulation shows what will happen when the transaction is executed without
 * actually sending it to the blockchain.
 *
 * **Important**: A successful simulation does not guarantee the transaction will succeed.
 * The simulation may succeed but indicate the transaction would fail (e.g., due to
 * insufficient balance). Check the `validation` field in the result for potential issues.
 *
 * @example
 * ```typescript
 * const result = await simulateSolanaTransaction({
 *   walletAccount,
 *   transaction,
 *   includeFees: true
 * });
 *
 * // Check if transaction would fail
 * if (result.validation?.reason?.includes('not enough SOL')) {
 *   console.log('Transaction would fail due to insufficient balance');
 * }
 * ```
 *
 * @param params.walletAccount - The Solana wallet account that will sign the transaction.
 * @param params.transaction - The Solana transaction (legacy or versioned) to simulate.
 * @param params.includeFees - Whether to calculate transaction fees (default: false).
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns Simulation result with asset diffs, security validation, and optional fee data.
 * @throws {InvalidParamError} If wallet account is not a Solana account.
 * @throws {SimulationFailedError} If the simulation API call fails.
 * @throws {FeeEstimationFailedError} If includeFees is true and fee calculation fails.
 */
export const simulateSolanaTransaction = async (
  {
    walletAccount,
    transaction,
    includeFees = false,
  }: SimulateSolanaTransactionParams,
  client = getDefaultClient()
): Promise<SolanaSimulationResult> => {
  // Validate wallet account type
  if (!isSolanaWalletAccount(walletAccount)) {
    throw new InvalidParamError(
      'Wallet account must be a Solana wallet account'
    );
  }

  // Get active network data for the wallet
  const { networkData } = await getActiveNetworkData({ walletAccount }, client);

  if (!networkData) {
    throw new InvalidParamError('Network data not found for wallet account');
  }

  try {
    // Serialize full transaction to base58 for API
    const transactionBytes = isVersionedTransaction(transaction)
      ? transaction.serialize()
      : transaction.serialize({
          requireAllSignatures: false,
          verifySignatures: false,
        });

    const serialized = bs58.encode(transactionBytes as Uint8Array);

    // eslint-disable-next-line no-console
    console.log('[SIMULATE SOLANA TX] Serialization details:', {
      accountAddress: walletAccount.address,
      isVersioned: isVersionedTransaction(transaction),
      serializedBase58: serialized,
      serializedLength: serialized.length,
      transactionLength: transactionBytes.length,
    });

    // Create API client and call simulation endpoint
    const core = getCore(client);
    const apiClient = createApiClient({}, client);

    const requestPayload = {
      environmentId: core.environmentId,
      simulateSVMTransactionRequest: {
        accountAddress: walletAccount.address,
        chain: 'solana',
        method: 'solana_signTransaction',
        transactions: [serialized],
      },
    };

    // eslint-disable-next-line no-console
    console.log('[SIMULATE SOLANA TX] API Request:', requestPayload);

    const response = await apiClient.simulateSVMTransaction(requestPayload);

    // Calculate fees if requested
    let feeData: SolanaSimulationResult['feeData'];

    if (includeFees) {
      // Check if transaction is sponsored (fee payer != wallet address)
      const walletPublicKey = new PublicKey(walletAccount.address);
      const feePayer = isVersionedTransaction(transaction)
        ? transaction.message.staticAccountKeys[0]
        : transaction.feePayer;

      const isSponsored = feePayer && !feePayer.equals(walletPublicKey);

      if (isSponsored) {
        // Sponsored transaction - no fee for user
        feeData = {
          humanReadableAmount: '0',
          nativeAmount: 0n,
          usdAmount: '0.00',
        };
      } else {
        // User pays fee - calculate it
        feeData = await calculateSolanaTransactionFee({
          nativeTokenPriceUsd: response.priceData?.nativeTokenUsdPrice,
          networkData,
          transaction,
        });
      }
    }

    // Map API response to simulation result
    return {
      counterparties: response.counterparties,
      feeData,
      inAssets: response.inAssets || [],
      outAssets: response.outAssets || [],
      priceData: response.priceData,
      showTotalFiat: response.showTotalFiat,
      validation: response.validation
        ? {
            description: response.validation.description,
            reason: response.validation.reason,
            result: response.validation.result as
              | 'benign'
              | 'warning'
              | 'malicious',
          }
        : undefined,
    };
  } catch (error) {
    // Re-throw FeeEstimationFailedError as-is
    if (error instanceof FeeEstimationFailedError) {
      throw error;
    }

    throw new SimulationFailedError({
      cause: error instanceof Error ? error : undefined,
      message: 'Failed to simulate Solana transaction',
    });
  }
};
